import { useCallback, useEffect, useState } from "react";
import {
  ApiFehler,
  aktiveBestellungen,
  bestellungAblehnen,
  bestellungAnnehmen,
  bestellungErledigen,
  verzoegerungErhoehen,
  verzoegerungZuruecksetzen,
  type Kanal,
  type KochBestellungAnsicht,
} from "../api";
import { BestellKarte } from "./BestellKarte";
import { VerzoegerungHebel } from "./VerzoegerungHebel";

const TOKEN_SCHLUESSEL = "ppz_dashboard_token";
const POLL_MS = 5000;

/** Das minimale Koch-Dashboard: Token → Live-Liste (Polling) → Übergänge + Verzögerungs-Hebel. */
export function Kueche() {
  const [token, setToken] = useState<string>(() => localStorage.getItem(TOKEN_SCHLUESSEL) ?? "");
  const [tokenEntwurf, setTokenEntwurf] = useState("");
  const [bestellungen, setBestellungen] = useState<KochBestellungAnsicht[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [verzoegerung, setVerzoegerung] = useState<Record<Kanal, number | null>>({
    LIEFERUNG: null,
    ABHOLUNG: null,
  });

  const laden = useCallback(async () => {
    if (!token) return;
    try {
      setBestellungen(await aktiveBestellungen(token));
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }, [token]);

  // Live-Liste: sofort laden und dann alle ~5 s pollen, solange ein Token gesetzt ist.
  useEffect(() => {
    if (!token) return;
    void laden();
    const id = window.setInterval(() => void laden(), POLL_MS);
    return () => window.clearInterval(id);
  }, [token, laden]);

  const tokenSpeichern = (e: React.FormEvent) => {
    e.preventDefault();
    const wert = tokenEntwurf.trim();
    if (!wert) return;
    localStorage.setItem(TOKEN_SCHLUESSEL, wert);
    setToken(wert);
    setTokenEntwurf("");
  };

  const abmelden = () => {
    localStorage.removeItem(TOKEN_SCHLUESSEL);
    setToken("");
    setBestellungen([]);
    setFehler(null);
  };

  /** Führt eine Koch-Aktion aus und lädt danach die Liste neu; Fehler werden gemeldet. */
  const aktion = async (lauf: () => Promise<unknown>) => {
    setBeschaeftigt(true);
    setFehler(null);
    try {
      await lauf();
      await laden();
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBeschaeftigt(false);
    }
  };

  const annehmen = (id: string) => aktion(() => bestellungAnnehmen(token, id));
  const erledigen = (id: string) => aktion(() => bestellungErledigen(token, id));
  const ablehnen = (id: string) => {
    const grund = window.prompt("Grund der Ablehnung?")?.trim();
    if (!grund) return;
    void aktion(() => bestellungAblehnen(token, id, grund));
  };

  const hebelErhoehen = (kanal: Kanal, schrittMin: number) =>
    aktion(async () => {
      const stand = await verzoegerungErhoehen(token, kanal, schrittMin);
      setVerzoegerung((alt) => ({ ...alt, [kanal]: stand.verzoegerungMin }));
    });

  const hebelZuruecksetzen = (kanal: Kanal) =>
    aktion(async () => {
      const stand = await verzoegerungZuruecksetzen(token, kanal);
      setVerzoegerung((alt) => ({ ...alt, [kanal]: stand.verzoegerungMin }));
    });

  if (!token) {
    return (
      <form className="token-form" onSubmit={tokenSpeichern}>
        <h2>Küchen-Dashboard</h2>
        <label className="feld">
          <span>Dashboard-Token</span>
          <input
            type="password"
            value={tokenEntwurf}
            onChange={(e) => setTokenEntwurf(e.target.value)}
            placeholder="Token eingeben"
            autoComplete="off"
          />
        </label>
        <button type="submit" className="knopf primaer">
          Anmelden
        </button>
      </form>
    );
  }

  return (
    <div className="kueche">
      <div className="kueche-kopf">
        <h2>Küche — offene Bestellungen</h2>
        <button type="button" className="knopf klein" onClick={abmelden}>
          Token entfernen
        </button>
      </div>

      {fehler && <p className="fehler">{fehler}</p>}

      <VerzoegerungHebel
        beschaeftigt={beschaeftigt}
        stand={verzoegerung}
        onErhoehen={hebelErhoehen}
        onZuruecksetzen={hebelZuruecksetzen}
      />

      {bestellungen.length === 0 ? (
        <p className="hinweis">Keine offenen Bestellungen.</p>
      ) : (
        <ul className="bestell-liste">
          {bestellungen.map((bestellung) => (
            <BestellKarte
              key={bestellung.id}
              bestellung={bestellung}
              beschaeftigt={beschaeftigt}
              onAnnehmen={annehmen}
              onAblehnen={ablehnen}
              onErledigen={erledigen}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Wandelt einen Fehler in lesbaren deutschen Text; ApiFehler trägt ihn bereits. */
function fehlerText(e: unknown): string {
  if (e instanceof ApiFehler) return e.message;
  if (e instanceof Error) return e.message;
  return "Unerwarteter Fehler.";
}
